import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const SubscriptionSection = () => {
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulation de chargement des données
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        // Simulation d'API call vers Supabase
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Données mockées (à remplacer par l'API Supabase)
        setSubscription({
          plan_name: 'Yuume Premium',
          status: 'active',
          start_date: '2025-01-15T10:00:00Z',
          next_billing: '2025-02-15T10:00:00Z',
          price: 12.99,
          currency: 'EUR'
        });

        setPaymentHistory([
          {
            id: '1',
            amount: 12.99,
            currency: 'EUR',
            status: 'completed',
            provider: 'Stripe',
            created_at: '2025-01-15T10:00:00Z'
          },
          {
            id: '2', 
            amount: 12.99,
            currency: 'EUR',
            status: 'completed',
            provider: 'Stripe',
            created_at: '2024-12-15T10:00:00Z'
          },
          {
            id: '3',
            amount: 12.99,
            currency: 'EUR', 
            status: 'pending',
            provider: 'PayPal',
            created_at: '2024-11-15T10:00:00Z'
          }
        ]);
      } catch (error) {
        console.error('Erreur chargement abonnement:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actif', color: 'bg-green-600', icon: CheckCircle },
      pending: { label: 'En attente', color: 'bg-yellow-600', icon: Clock },
      cancelled: { label: 'Annulé', color: 'bg-red-600', icon: XCircle },
      completed: { label: 'Payé', color: 'bg-green-600', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white flex items-center space-x-1`}>
        <Icon className="h-3 w-3" />
        <span>{config.label}</span>
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Abonnement</h1>
        <Button className="flex items-center space-x-2 bg-pink-600 hover:bg-pink-700">
          <ExternalLink className="h-4 w-4" />
          <span>Gérer mon abonnement</span>
        </Button>
      </div>

      {/* Abonnement actuel */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <CreditCard className="h-6 w-6 text-pink-400" />
          <span>Abonnement actuel</span>
        </h2>

        {subscription ? (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Informations de l'abonnement */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Plan</span>
                <span className="text-white font-semibold text-xl">{subscription.plan_name}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Statut</span>
                {getStatusBadge(subscription.status)}
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Prix</span>
                <span className="text-white font-semibold">
                  {subscription.price}€ / mois
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Date de début</span>
                <span className="text-white">{formatDate(subscription.start_date)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Prochaine facturation</span>
                <span className="text-white">{formatDate(subscription.next_billing)}</span>
              </div>
            </div>

            {/* Avantages du plan */}
            <div className="bg-gradient-to-r from-pink-600/10 to-violet-600/10 border border-pink-500/20 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Avantages inclus</span>
              </h3>
              
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Accès illimité à tous les contenus IA</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Streaming 4K Ultra HD</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Téléchargement hors ligne</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Jusqu'à 5 profils</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Support prioritaire</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-4">Aucun abonnement actif</p>
            <Button className="bg-pink-600 hover:bg-pink-700">
              Choisir un plan
            </Button>
          </div>
        )}
      </div>

      {/* Historique des paiements */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
          <Calendar className="h-6 w-6 text-violet-400" />
          <span>Historique des paiements</span>
        </h2>

        {paymentHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 py-3 px-4">Date</th>
                  <th className="text-left text-gray-400 py-3 px-4">Montant</th>
                  <th className="text-left text-gray-400 py-3 px-4">Méthode</th>
                  <th className="text-left text-gray-400 py-3 px-4">Statut</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                    <td className="text-white py-4 px-4">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="text-white py-4 px-4 font-semibold">
                      {payment.amount}€
                    </td>
                    <td className="text-gray-300 py-4 px-4">
                      {payment.provider}
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(payment.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400">Aucun paiement enregistré</p>
          </div>
        )}
      </div>

      {/* Actions rapides */}
      <div className="grid md:grid-cols-3 gap-4">
        <Button variant="outline" className="p-6 h-auto flex-col space-y-2">
          <CreditCard className="h-6 w-6 text-pink-400" />
          <span>Changer de plan</span>
        </Button>
        
        <Button variant="outline" className="p-6 h-auto flex-col space-y-2">
          <Calendar className="h-6 w-6 text-violet-400" />
          <span>Suspendre</span>
        </Button>
        
        <Button variant="outline" className="p-6 h-auto flex-col space-y-2 border-red-600 text-red-400 hover:bg-red-600/10">
          <XCircle className="h-6 w-6" />
          <span>Annuler</span>
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionSection;